import { memo, useMemo } from 'react'
import './IPRProgress.css'

// Выделяем прогресс-бар каждого таланта в отдельный мемоизированный компонент
interface TalentProgressBarProps {
  talentId: string
  talentName: string
  progress: number
}
const TalentProgressBar = memo((props: TalentProgressBarProps) => {
  return (
    <div key={props.talentId} className="ipr-talent-item">
      <div className="ipr-talent-info">
        <div className="ipr-talent-name">{props.talentName}</div>
        <div className="ipr-talent-percent">{props.progress}%</div>
      </div>
      <div className="ipr-talent-bar">
        <div className="ipr-talent-bar-fill" style={{ width: `${props.progress}%` }}></div>
      </div>
    </div>
  )
})

// Выделяем круговую диаграмму в отдельный компонент
interface ProgressCircleProps {
  progress: number
}
const ProgressCircle = memo((props: ProgressCircleProps) => {
  const dashOffset = useMemo(() => {
    return 339.292 * (1 - props.progress / 100)
  }, [props.progress])

  return (
    <svg width="120" height="120" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="54" fill="none" stroke="#e9ecef" strokeWidth="12" />
      <circle
        cx="60"
        cy="60"
        r="54"
        fill="none"
        stroke="#10BDC8"
        strokeWidth="12"
        strokeDasharray="339.292"
        strokeDashoffset={dashOffset}
        transform="rotate(-90 60 60)"
      />
      <text
        x="60"
        y="60"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize="24"
        fontWeight="bold"
        fill="#333"
      >
        {props.progress}%
      </text>
    </svg>
  )
})

// Основной компонент прогресса
const IPRProgress = ({ iprs }) => {
  const { totalProgress, sortedIPRs } = useMemo(() => {
    if (iprs.length === 0) {
      return { totalProgress: 0, sortedIPRs: [] }
    }
    const total = Math.round(iprs.reduce((sum, ipr) => sum + ipr.progress, 0) / iprs.length)
    const sorted = [...iprs].sort((a, b) => b.progress - a.progress).slice(0, 15)
    return { totalProgress: total, sortedIPRs: sorted }
  }, [iprs])

  if (iprs.length === 0) {
    return (
      <div className="ipr-progress-empty">
        <h3>Нет созданных планов развития</h3>
        <p>
          Создайте индивидуальный план развития (ИПР) для ваших талантов, чтобы отслеживать
          прогресс.
        </p>
      </div>
    )
  }

  // Отображаем только первые 15 планов для улучшения производительности
  const hasMoreItems = iprs.length > 15

  return (
    <div className="ipr-progress">
      <div className="ipr-progress-summary">
        <div className="ipr-progress-circle">
          <ProgressCircle progress={totalProgress} />
        </div>
        <div className="ipr-progress-info">
          <h3>Общий прогресс развития</h3>
          <p>Выполнено {totalProgress}% всех планов развития</p>
          <p className="ipr-count">Всего планов: {iprs.length}</p>
        </div>
      </div>

      <div className="ipr-progress-list">
        <h4>Прогресс по талантам</h4>
        <div className="ipr-talent-progress">
          {sortedIPRs.map((ipr) => (
            <TalentProgressBar
              key={ipr.talentId}
              talentId={ipr.talentId}
              talentName={ipr.talentName}
              progress={ipr.progress}
            />
          ))}

          {hasMoreItems && (
            <div className="ipr-talent-more">
              <p>... и еще {iprs.length - 15} планов</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Оборачиваем в memo для предотвращения ненужных рендеров
export default memo(IPRProgress)
